import { ReactElement, ReactNode, useEffect } from 'react'
import { buildErrorMessage } from '@constants'
import { AxiosError } from 'axios'
import produce from 'immer'
import { cloneDeep } from 'lodash-es'
import { v4 } from 'uuid'
import create from 'zustand'
import { combine } from 'zustand/middleware'
import { useQuery } from '@tanstack/react-query'
import { useSnackbar } from '@ui/snackbar'
import { getComments } from '@queries'
import { usePersistentParams } from 'src/hooks'
import { validate } from '../middleware'

interface Props {
  children: ReactNode
}

interface CommentsInitialState {
  comments: Comment3D[]
}

interface CommentsStoreType extends CommentsInitialState {
  clear: () => void
  addComment: (comment: Comment3D) => void
  updateComment: (id: string, comment: Partial<Comment3D>) => void
  removeComment: (id: string) => void
  duplicateComment: (id: string) => void
  setComments: (comments: Comment3D[]) => void
}

const initialState: CommentsInitialState = {
  comments: [],
}

const createStore = () =>
  create<CommentsStoreType>(
    validate(
      combine(cloneDeep(initialState), set => ({
        clear: () => set({ comments: [] }),

        addComment: (comment: Comment3D) =>
          set(
            produce(state => {
              state.comments.push(comment)
            }),
          ),

        updateComment: (id: string, comment: Partial<Comment3D>) =>
          set(
            produce(state => {
              const index = state.comments.findIndex((c: Comment3D) => c.id === id)
              if (index !== -1) {
                state.comments[index] = { ...state.comments[index], ...comment }
                // Remove properties that are explicitly set to undefined
                Object.keys(comment).forEach(key => {
                  if (comment[key as keyof Comment3D] === undefined) {
                    delete state.comments[index][key as keyof Comment3D]
                  }
                })
              }
            }),
          ),

        removeComment: (id: string) =>
          set(
            produce(state => {
              state.comments = state.comments.filter((c: Comment3D) => c.id !== id)
            }),
          ),

        duplicateComment: (id: string) =>
          set(
            produce(state => {
              const comment = state.comments.find((c: Comment3D) => c.id === id)
              if (comment) {
                const duplicatedComment: Comment3D = {
                  ...comment,
                  id: v4(),
                  text: `Kopie ${comment.text}`,
                  position: {
                    ...comment.position,
                    z: comment.position.z + 0.25,
                  },
                }
                state.comments.push(duplicatedComment)
              }
            }),
          ),

        setComments: (comments: Comment3D[]) => set({ comments }),
      })),
    ),
  )

const useCommentsStore = createStore()

const CommentsStoreProvider = ({ children }: Props): ReactElement => {
  const clear = useCommentsStore(state => state.clear)
  const setComments = useCommentsStore(state => state.setComments)
  const { projectId }: { projectId?: string } = usePersistentParams()
  const { enqueueSnackbar } = useSnackbar()

  // Load comments from backend on mount
  useQuery({
    queryKey: getComments.getKey(projectId),
    queryFn: () => getComments.request(projectId),
    enabled: !!projectId,
    onSuccess: data => {
      if (data) {
        setComments(data)
      } else {
        setComments([])
      }
    },
    onError: (error: AxiosError) => {
      // Silently fail if no data exists yet (404 is expected on first use)
      if (error.response?.status === 404) {
        setComments([])
      } else {
        enqueueSnackbar(buildErrorMessage(error, 'Fehler beim Laden der Kommentare'), {
          variant: 'error',
        })
      }
    },
  })

  // Only the loaded comments are dropped here — a comment *selection* lives in the selection
  // store, whose provider (mounted alongside this one) clears it on editor unmount itself.
  useEffect(() => () => clear(), [clear])

  return <>{children}</>
}

export { CommentsStoreProvider, useCommentsStore }
